// @ts-strict-ignore
import { memo, useCallback, useEffect, useRef } from 'react';
import { alpha, getOverlayAlpha, useTheme } from '@mui/material';
import * as d3 from 'd3';
import wrapMarkDownText from '../../services/wrapMarkDownText';
import getParentSizeFromRef from '../../services/getParentSizeFromRef';
import useWindowSize from '../../services/useWindowSize';

type Wide = boolean;
export type Data = {
    label: string;
    rating: number;
    improve?: number;
    p75: number;
    isMain?: boolean;
};

type BarChartProps = {
    data: Array<Data>;
    wide?: Wide;
    isPQi?: boolean;
};

function BarChart({ data, wide = false, isPQi = false }: BarChartProps) {
    const svgRef = useRef();
    // Causes re-render on resize
    const windowSize = useWindowSize(250);
    const theme = useTheme();

    const render = useCallback(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('g').remove();
        const parentSize = getParentSizeFromRef(svgRef);
        if (!parentSize) {
            return;
        }
        const { parentWidth, parentHeight } = parentSize;

        const margin = {
            top: 0,
            right: wide ? 16 : 80,
            bottom: 30, // for the y-axis tick labels
            left: wide ? 16 : 80,
        };
        const xLabelWidth = parentWidth * (wide ? 0.5 : 0.4) - margin.left - 16;
        const dataLength = data.length;
        const rowHeight =
            (parentHeight - margin.top - margin.bottom) / dataLength;
        const width = parentWidth;
        const height = rowHeight * dataLength;

        const plotLeft = margin.left + xLabelWidth + 16;

        /////////////////////////////////////////////////////////
        // START CREATE AXIS///
        const xScale = d3
            .scaleLinear()
            .domain([0, 100])
            .range([plotLeft, width - margin.right]);

        const yScale = d3
            .scaleLinear()
            .domain([0, dataLength])
            .range([0, height]);
        const ticks = xScale.ticks(10);

        const xTickValues = d3.range(
            xScale.domain()[0],
            1 + xScale.domain()[1],
            10,
        );
        const xAxis = d3
            .axisBottom(xScale)
            .tickPadding(10)
            .ticks(xTickValues.length, 'd')
            .tickValues(xTickValues);

        data.forEach((_, i) => {
            const top = yScale(i);
            const bottom = yScale(i + 1);

            if (i % 2 === 1) {
                //Gray and white bar background
                svg.append('g')
                    .append('rect')
                    .attr('x', 0)
                    .attr('width', parentWidth)
                    .attr('y', top)
                    .attr('height', bottom - top)
                    .attr(
                        'fill',
                        alpha(
                            theme.palette.mode === 'dark' ? '#fff' : '#000',
                            parseFloat(getOverlayAlpha(1)),
                        ),
                    );
            }
        });

        svg.append('g')
            .selectAll('text')
            .data(ticks)
            .enter()
            .append('text')
            .attr('font-size', '16') // what about font family?
            .attr('fill', `${theme.bino.color.gray}`)
            .attr('y', height + margin.bottom - 1)
            .attr('x', (d) => xScale(d))
            .attr('text-anchor', 'middle');

        svg.append('g')
            .selectAll('line')
            .data(ticks)
            .enter()
            .append('line')
            .attr('stroke', `${theme.bino.color.gray}`)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', `5 5`)
            .attr('x1', (d) => xScale(d) - (d === 100 ? 0.5 : 0))
            .attr('x2', (d) => xScale(d) - (d === 100 ? 0.5 : 0))
            .attr('y1', 0);

        const plot = svg.append('g');

        // ----------- Scale ----------------- //
        svg.append('g')
            .selectAll('line')
            .data(xScale.ticks())
            .enter()
            .append('line')
            .attr('stroke', `#575757`)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', `4 4`)
            .attr('opacity', 0.5)
            .attr('x1', xScale)
            .attr('x2', xScale)
            .attr('y1', yScale.range()[0])
            .attr('y2', yScale.range()[1]);

        svg.append('g')
            .attr('stroke', 'none')
            .attr('stroke-width', 0)
            .attr('transform', `translate(0,  ${yScale.range()[1]})`)
            .call(xAxis)
            .selectAll('text')
            .attr('font-size', 12)
            .attr('fill', theme.bino.color.gray)
            .attr('stroke', 'none');
        // ------------------------------------------

        // ------------- Draw functions -------------

        const drawLine = (values: Data[]) => {
            values.forEach(({ rating, improve, p75, isMain }, index) => {
                const barHeight = 14;
                const top = yScale(index);
                const bottom = yScale(index + 1);
                const middle = top + (bottom - top) / 2;

                const p75Line = d3.path();
                if (p75 !== undefined) {
                    //straight line top to bottom
                    p75Line.moveTo(xScale(p75), top);
                    p75Line.lineTo(xScale(p75), bottom);

                    p75Line.moveTo(xScale(p75), bottom);
                    if (data[index + 1]) {
                        p75Line.lineTo(xScale(data[index + 1].p75), bottom);
                    }
                }
                const bestPracticeColor = theme.bino.color.green;

                //Grey background bar
                plot.append('rect')
                    .attr('x', xScale(0))
                    .attr('y', middle - barHeight / 2)
                    .attr('rx', 4)
                    .attr('ry', 4)
                    .attr('width', xScale(100) - xScale(0))
                    .attr('height', barHeight)
                    .attr('fill', index % 2 ? '#d2d2d2' : '#eeeeee');

                plot.append('rect')
                    .attr('x', xScale(p75))
                    .attr('y', top)
                    .attr('width', xScale(100) - xScale(p75))
                    .attr('height', rowHeight)
                    .attr('fill', bestPracticeColor)
                    .attr('fill-opacity', 0.2);

                if (p75 !== undefined) {
                    plot.append('path')
                        .attr('d', p75Line.toString())
                        .attr('stroke-width', 2)
                        .attr('stroke', bestPracticeColor)
                        .attr('stroke-dasharray', `5 5`);
                }
                //Purple rating bar
                plot.append('rect')
                    .attr('x', xScale(0))
                    .attr('y', middle - barHeight / 2)
                    .attr('rx', 4)
                    .attr('ry', 4)
                    .attr('width', xScale(rating) - xScale(0))
                    .attr('height', barHeight)
                    .attr(
                        'fill',
                        isMain
                            ? theme.bino.color.primary
                            : theme.bino.color.gray,
                    );

                //Rating Circle
                plot.append('circle')
                    .attr('r', barHeight)
                    .attr('cx', xScale(rating))
                    .attr('cy', middle)
                    .attr(
                        'stroke',
                        isMain
                            ? theme.bino.color.primary
                            : theme.bino.color.gray,
                    )
                    .attr(
                        'fill',
                        isMain
                            ? theme.bino.color.primary
                            : theme.bino.color.gray,
                    );

                //Improve line
                if (improve != null) {
                    if (improve >= 1 && improve !== rating) {
                        plot.append('line')
                            .attr('x1', xScale(improve))
                            .attr('x2', xScale(improve))
                            .attr('y1', middle - barHeight / 2)
                            .attr('y2', middle + barHeight / 2)
                            .attr('stroke', theme.bino.color.white)
                            .attr('stroke-width', 2);
                    }
                    const improveOffset =
                        improve === rating ? -barHeight / 2 : 0;

                    //Improve circle
                    plot.append('circle')
                        .attr('r', 8)
                        .attr('cx', xScale(improve))
                        .attr('cy', middle - barHeight / 2 + improveOffset)
                        .attr('fill', theme.bino.color.red);
                    plot.append('circle')
                        .attr('r', 8 - 1.5)
                        .attr('cx', xScale(improve))
                        .attr('cy', middle - barHeight / 2 + improveOffset)
                        .attr('stroke', theme.bino.color.white)
                        .attr('fill', 'transparent')
                        .attr('stroke-width', 3);
                }

                //Circle Text
                plot.append('text')
                    .attr('font-size', barHeight) // what about font family?
                    .attr('stroke', theme.bino.color.white)
                    .attr('fill', theme.bino.color.white)
                    .attr('x', xScale(rating) - barHeight / 2)
                    .attr('y', middle)
                    .attr('dy', '0.35em')
                    .attr('text-align', 'center')
                    .attr('width', barHeight * 2)
                    .text(rating);
            });
        };

        const drawText = (values: Data[]) => {
            svg.append('g')
                .selectAll('text')
                .data(values)
                .enter()
                .append('text')
                .append('tspan')
                .text((d) => (isPQi ? d.label.toUpperCase() : d.label))
                .attr('fill', (d) =>
                    d.isMain
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary,
                )
                .attr('x', margin.left)
                .attr(
                    'y',
                    (_d, i) => yScale(i) + (yScale(i + 1) - yScale(i)) / 2,
                )
                .attr('dy', '0.25em')
                .attr('width', xLabelWidth)
                .attr('height', yScale(1))
                .attr('font-weight', isPQi ? 'bold' : 300)
                .call(wrapMarkDownText);
        };

        // -------------

        drawLine(data);
        drawText(data);
    }, [data, wide, isPQi, theme]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: We need windowSize to re-render on resize
    useEffect(render, [render, windowSize]);
    return <svg ref={svgRef} width="100%" height="100%" overflow="visible" />;
}

const BarChartMemo = memo(BarChart);
export default BarChartMemo;
